import { useCallback, useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { useIsCompact } from '../lib/useMediaQuery'

/**
 * Two columns with a draggable divider.
 *
 * The handle is invisible until you approach it — a permanent rule between two
 * panels reads as a border and adds a line to a page that is already mostly
 * lines. On hover or while dragging it becomes an accent bar, which is the only
 * moment it needs to exist.
 *
 * The width is stored per `storageKey`, so someone who wants the flights column
 * wide keeps it that way between sessions rather than re-dragging every visit.
 */
export default function SplitPane({
  storageKey,
  defaultRailWidth,
  minRailWidth = 280,
  maxRailFraction = 0.55,
  primary,
  rail,
}: {
  storageKey: string
  defaultRailWidth: number
  minRailWidth?: number
  /** Ceiling as a share of the container, so the rail can't swallow the page. */
  maxRailFraction?: number
  primary: ReactNode
  rail: ReactNode
}) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [railWidth, setRailWidth] = useState(() => readStored(storageKey) ?? defaultRailWidth)
  const [dragging, setDragging] = useState(false)
  // Below this there's no room for two columns, and a draggable divider on a
  // touch screen is a gesture nobody is looking for — it becomes a stack.
  const compact = useIsCompact()

  const clamp = useCallback(
    (width: number) => {
      const total = containerRef.current?.clientWidth ?? 1200
      return Math.max(minRailWidth, Math.min(width, Math.round(total * maxRailFraction)))
    },
    [minRailWidth, maxRailFraction],
  )

  // Pointer capture keeps the drag alive when the cursor outruns the handle,
  // which it always does — without it the resize stops the moment you move
  // faster than React re-renders.
  const onPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    event.preventDefault()
    event.currentTarget.setPointerCapture(event.pointerId)
    setDragging(true)
  }

  const onPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!dragging || !containerRef.current) return
    const bounds = containerRef.current.getBoundingClientRect()
    setRailWidth(clamp(bounds.right - event.clientX))
  }

  const endDrag = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!dragging) return
    event.currentTarget.releasePointerCapture(event.pointerId)
    setDragging(false)
    writeStored(storageKey, railWidth)
  }

  // A drag that starts over the handle shouldn't select the text either side.
  useEffect(() => {
    if (!dragging) return
    const previous = document.body.style.userSelect
    document.body.style.userSelect = 'none'
    document.body.style.cursor = 'col-resize'
    return () => {
      document.body.style.userSelect = previous
      document.body.style.cursor = ''
    }
  }, [dragging])

  // Keep the rail legal when the window changes under it.
  useEffect(() => {
    const onResize = () => setRailWidth((current) => clamp(current))
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [clamp])

  function nudge(delta: number) {
    setRailWidth((current) => {
      const next = clamp(current + delta)
      writeStored(storageKey, next)
      return next
    })
  }

  if (compact) {
    return (
      <div className="stack" style={{ gap: 28 }}>
        <div style={{ minWidth: 0 }}>{primary}</div>
        <div style={{ minWidth: 0 }}>{rail}</div>
      </div>
    )
  }

  return (
    <div
      ref={containerRef}
      style={{
        display: 'grid',
        gridTemplateColumns: `minmax(0, 1fr) 14px ${railWidth}px`,
        alignItems: 'start',
      }}
    >
      <div style={{ minWidth: 0 }}>{primary}</div>

      <div
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize columns"
        aria-valuenow={railWidth}
        tabIndex={0}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onDoubleClick={() => {
          setRailWidth(defaultRailWidth)
          writeStored(storageKey, defaultRailWidth)
        }}
        onKeyDown={(event) => {
          // Keyboard users get the same control; a mouse-only resize is a
          // resize most people can't reach.
          if (event.key === 'ArrowLeft') nudge(24)
          if (event.key === 'ArrowRight') nudge(-24)
        }}
        title="Drag to resize · double-click to reset"
        style={{
          position: 'relative',
          cursor: 'col-resize',
          alignSelf: 'stretch',
          // The hit area is the full 14px; the visible line is 2px inside it.
          display: 'flex',
          justifyContent: 'center',
        }}
        className="split-handle"
      >
        <span
          aria-hidden
          style={{
            width: 2,
            borderRadius: 2,
            height: '100%',
            background: dragging ? 'var(--accent)' : 'transparent',
            transition: 'background-color 0.15s var(--ease)',
          }}
        />
      </div>

      <div style={{ minWidth: 0 }}>{rail}</div>
    </div>
  )
}

function readStored(key: string): number | null {
  try {
    const raw = localStorage.getItem(key)
    const value = raw ? Number(raw) : NaN
    return Number.isFinite(value) ? value : null
  } catch {
    return null
  }
}

function writeStored(key: string, value: number) {
  try {
    localStorage.setItem(key, String(value))
  } catch {
    /* private mode — the width just won't persist */
  }
}
